import { motion } from 'motion/react';
import { Container } from '@/components/ui/Container';
import { Card } from '@/components/ui/Card';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { staggerContainer, fadeInUp } from '@/lib/animations';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export function PainPoints() {
  const { t } = useTranslation();
  const { ref, isInView } = useScrollAnimation();

  const painPoints = [
    {
      icon: MagnifyingGlassIcon,
      title: t('painPoints.searching.title'),
      stat: t('painPoints.searching.stat'),
      description: t('painPoints.searching.description'),
      color: 'text-red-500',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
    },
    {
      icon: DocumentDuplicateIcon,
      title: t('painPoints.recreating.title'),
      stat: t('painPoints.recreating.stat'),
      description: t('painPoints.recreating.description'),
      color: 'text-orange-500',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
    },
    {
      icon: QuestionMarkCircleIcon,
      title: t('painPoints.asking.title'),
      stat: t('painPoints.asking.stat'),
      description: t('painPoints.asking.description'),
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
    },
    {
      icon: ExclamationTriangleIcon,
      title: t('painPoints.wrongVersions.title'),
      stat: t('painPoints.wrongVersions.stat'),
      description: t('painPoints.wrongVersions.description'),
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 dark:bg-slate-900/50">
      <Container>
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section header */}
          <motion.div variants={fadeInUp} className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              {t('painPoints.title')}{' '}
              <span className="text-red-500">{t('painPoints.titleHighlight')}</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
              {t('painPoints.subtitle')}
            </p>
          </motion.div>

          {/* Pain point cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {painPoints.map((pain, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full text-center" hover>
                  <div className={`w-14 h-14 ${pain.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <pain.icon className={`w-7 h-7 ${pain.color}`} />
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {pain.stat}
                  </p>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {pain.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    {pain.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Total impact */}
          <motion.div
            variants={fadeInUp}
            className="mt-12 text-center"
          >
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-8 px-8 py-6 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-200 dark:border-red-800">
              <div>
                <p className="text-4xl sm:text-5xl font-bold text-red-600 dark:text-red-400">
                  5-10 hrs
                </p>
                <p className="text-red-600 dark:text-red-400 font-medium">
                  {t('painPoints.totalImpact.hoursLost')}
                </p>
              </div>
              <div className="hidden sm:block w-px h-16 bg-red-200 dark:bg-red-800" />
              <div>
                <p className="text-4xl sm:text-5xl font-bold text-red-600 dark:text-red-400">
                  $250-1,000
                </p>
                <p className="text-red-600 dark:text-red-400 font-medium">
                  {t('painPoints.totalImpact.productivityLost')}
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
}
